import { Fragment, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Container, Divider, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../routing/LangRouter";

//import utils
import { fixNumbers, scrollToPosition } from "../../utils/utility";

//import constants
import { IS_MOBILE } from "../../constants/breakpoints";

//import general component
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import BaseTitle from "../../components/common/base-title/base-title";
import { BaseButton } from "../../components/common/base-button/base-button";
import OldPassengersModal from "../../components/view/passengers-forms/old-passengers-modal";
import LoginModal from "../../components/common/login-modal/login-modal";
import UserInformationModal from "../../components/common/user-information-login/user-information-modal";
import TabPanel from "../../components/common/base-tab/base-tabs-panel";
import BaseTabPanel from "../../components/common/base-tab/base-tab-panel";

//import view components
import PassengersFormContainer from "../../components/view/passengers-forms/passengers-form-container";
import RulesLink from "../rules/rules-link";

//import images and icons
import Icon from "../../components/common/base-icon/icon";

//import styles
import "./passengers.scss";
import { formatPersianDate } from "../../utils/date";
import SelectedItem from "../../components/common/selected-item/selected-item";

export default function HotelPassengers() {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const accessToken = localStorage.getItem("accessToken");
    // const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isUserInformationOpen, setIsUserInformationOpen] = useState(false);

    //sessionStorage variables
    const userInformation = JSON.parse(localStorage.getItem("userInformation"));
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight"));
    const selectedRooms = JSON.parse(sessionStorage.getItem("selectedRooms"));
    const hotelPassengers = JSON.parse(sessionStorage.getItem("hotelPassengers"));
    const selectedHotel = JSON.parse(sessionStorage.getItem("selectedHotel"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));

    useEffect(() => {
        scrollToPosition(0, 0);
    }, []);

    const flatRoomList = selectedRooms.flatMap(item => Array(item.count).fill(item));
    const defaultValues = {
        passengers: flatRoomList.map(() => {
            return {
                id: "",
                rate_plan_id: "",
                children: "",
                roomsCount: "",
                extend_price: "",
                first_name: "",
                last_name: "",
                phone_number: "",
                email: "",
                national_code: "",
                passport_no: "",
                nationality: "",
            };
        }),
    };

    const {
        control,
        register,
        handleSubmit,
        watch,
        setValue,
        getValues,
        resetField,
        clearErrors,
        formState: { errors },
    } = useForm({
        defaultValues: hotelPassengers || defaultValues,
        mode: "all",
        delayError: 500,
    });

    const { fields: forms } = useFieldArray({
        control,
        name: "passengers",
    });

    const handleRegistration = data => {
        if (IS_MOBILE) {
            // Create array of required fields
            const requiredFieldsFN = ["first_name", "last_name", "phone_number", "nationality"];
            const requiredFieldsEN = ["phone_number", "nationality", "passport_no"];

            // Validate each passenger form
            const isValid = data.passengers.every((formData, index) => {

                // Check required fields
                const requiredFields = formData.nationality.id === 1 ? requiredFieldsFN : requiredFieldsEN;
                const isFormValid = requiredFields.every(field => {
                    return formData[field] !== "";
                   
                });
               
                if (!isFormValid) {
                    setTabValue(index);
                    toast.error(t("Toast Messages.Complete Forms for Each Room"), { toastId: "custom-id-yes" });
                    return false;
                }
                return true;
            });

            if (!isValid) return;
        }

        const convertedData = {
            ...data,
            passengers: data.passengers.map(passenger => ({
                ...passenger,
                email: fixNumbers(passenger.email),
                phone_number: fixNumbers(passenger.phone_number),
                national_code: fixNumbers(passenger.national_code),
                passport_no: fixNumbers(passenger.passport_no),
            })),
        };

        if (accessToken) {
            if (!!userInformation?.first_name && !!userInformation?.last_name && !!userInformation?.phone_number) {
                sessionStorage.setItem("hotelPassengers", JSON.stringify(convertedData));
                navigate(`/${locale}/confirm-information`);
            } else {
                setSearchParams({ action: "userInformation" });
            }
        } else {
            setSearchParams({ action: "login" });
        }
    };

    const handleError = error => {
        console.log(error);
    };

    //value for id and rate plan id rooms
    useEffect(() => {
        setTimeout(() => {
            flatRoomList.forEach((room, index) => {
                setValue(`passengers.${index}.id`, room?.roomId);
                setValue(`passengers.${index}.rate_plan_id`, room?.planId);
            });
        }, 1000);
    }, []);

    //handel the selection of passenger from previous passenger
    const [IDcard, setIDcard] = useState([]);
    const [isPassengersModalOpen, setIsPassengersModalOpen] = useState(false);
    const handleSelectPassenger = (passenger, index) => {
        clearErrors("passengers");
        setValue(
            `passengers.${index}`,
            {
                id: flatRoomList[index]?.roomId,
                rate_plan_id: flatRoomList[index]?.planId,
                first_name: passenger.first_name,
                last_name: passenger.last_name,
                phone_number: passenger.phone_number,
                email: passenger?.email || "",
                nationality: passenger.nationality,
                gender: passenger.gender,
                roomsCount: { adult: 1, child: 0, childAges: [] },
                children: [],
                extend_price: 0,
            },
            { shouldValidate: true },
        );

        if (IDcard[index].nationalCode) {
            setValue(`passengers.${index}.national_code`, passenger.national_code, { shouldValidate: true });
        } else {
            setValue(`passengers.${index}.passport_no`, passenger.passport_no, { shouldValidate: true });
        }
    };

    //handle modals
    useEffect(() => {
        if (searchParams.get("action") === "userInformation") setIsUserInformationOpen(true);
        else setIsUserInformationOpen(false);

        if (searchParams.get("action") === "old-passengers") setIsPassengersModalOpen(true);
        else setIsPassengersModalOpen(false);

        // if (searchParams.get("action") === "login") setIsLoginOpen(true);
        // else setIsLoginOpen(false);
    }, [searchParams]);

    //handle form tabs on mobile
    const [tabValue, setTabValue] = useState(0);
    const handleTabChange = (event, newValue) => {
        if (!errors.passengers?.[tabValue]) {
            scrollToPosition(0, 0);
            setTabValue(newValue);
        }
    };

    //handle navigate back on Mobile
    const navigateBack = () => {
        if (isItinerary) {
            if (selectedFlight) {
                navigate(`/${locale}/flight-passengers`, { replace: true });
            } else {
                navigate(`/${locale}/itinerary/hotel`, { replace: true });
            }
        } else navigate(`/${locale}/hotel`, { replace: true });
    };

    //selected item data used for display and navigation
    const data = {
        title: selectedHotel.name,
        star: selectedHotel.star,
        name: [flatRoomList?.length + " " + t("room")],
        image: "https://api.atripa.ir" + selectedHotel?.images?.[0]?.image,
        date: `${formatPersianDate(hotelInformation.checkIn, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: "long",
        })} ${t("to-2")} ${formatPersianDate(hotelInformation.checkOut, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: "long",
        })}`,
        totalPrice: flatRoomList.reduce((accumulator, currentValue) => accumulator + currentValue.prices.total_price, 0),
        type: "hotel",
        details: flatRoomList,
        route: "hotel",
    };

    const sumWithInitial = flatRoomList.reduce((accumulator, currentValue) => accumulator + currentValue.prices.total_price, 0);

    return (
        <Container maxWidth="xl">
            <Grid className="passengers" container item xs={12} rowGap={3} mb={forms?.length > 1 ? 30 : 26}>
                <MobileHeader title={t("roomsInformation")} justifyContent="center" handleClick={navigateBack} />

                <Grid container item xs={12} rowGap={3} columnGap={1} mt={IS_MOBILE ? 7 : 0}>
                    <SelectedItem data={data} />
                </Grid>

                <Grid component="form" container item xs={12} rowGap={3} mt={2} onSubmit={handleSubmit(handleRegistration, handleError)}>
                    {IS_MOBILE ? (
                        <Grid className="passengers__forms" container item xs={12}>
                            {forms.map((form, formIndex) => {
                                return (
                                    <TabPanel value={tabValue} index={formIndex}>
                                        <PassengersFormContainer
                                            formIndex={formIndex}
                                            formType="hotel"
                                            passengerType={t("supervisor")}
                                            forms={forms}
                                            form={form}
                                            formCount={flatRoomList}
                                            IDcard={IDcard}
                                            setIDcard={setIDcard}
                                            {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
                                        />
                                    </TabPanel>
                                );
                            })}

                            {forms?.length > 1 ? (
                                <BaseTabPanel
                                    list={forms}
                                    tabValue={tabValue}
                                    handleTabChange={handleTabChange}
                                    isError={errors.passengers?.[tabValue]}
                                />
                            ) : null}
                        </Grid>
                    ) : (
                        <>
                            <BaseTitle
                                title={
                                    <Grid container item xs={12} columnGap={1} justifyContent="space-between" alignItems="center">
                                        <Icon name="BedroomIcon" /> {t("roomsInformation")}
                                    </Grid>
                                }
                                dividerColor="1px solid var(--medium-gray-color)"
                            />
                            <Grid className="passengers__forms" container item xs={12} rowGap={5}>
                                {forms.map((form, formIndex) => {
                                    return (
                                        <Fragment key={form.id}>
                                            <PassengersFormContainer
                                                formIndex={formIndex}
                                                formType="hotel"
                                                passengerType={t("supervisor")}
                                                form={form}
                                                formCount={flatRoomList}
                                                IDcard={IDcard}
                                                setIDcard={setIDcard}
                                                {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
                                            />
                                            {formIndex < forms.length - 1 && <Divider width="100%" />}
                                        </Fragment>
                                    );
                                })}
                            </Grid>
                        </>
                    )}
                    <Grid
                        className="passengers__submit-information"
                        container
                        item
                        xs={12}
                        rowGap={2}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <RulesLink />
                        <Grid container item xs={12} md={2} justifyContent="flex-end">
                            <BaseButton text={t("submitInformation")} color="primary" size="large" radius="radius-10" type="submit" />
                        </Grid>
                    </Grid>
                </Grid>

                {/* <LoginModal open={isLoginOpen} setOpen={setIsLoginOpen} /> */}
                <UserInformationModal open={isUserInformationOpen} setOpen={setIsUserInformationOpen} />
                <OldPassengersModal open={isPassengersModalOpen} setOpen={setIsPassengersModalOpen} selectedOldPassenger={handleSelectPassenger} />
            </Grid>
        </Container>
    );
}
